import type * as React from 'react';
import type { NavigateFunction } from 'react-router-dom';

/**
 * Sends a click anywhere on a row to `to`, honoring the modifiers an anchor
 * would.
 *
 * A row is a `<tr>`, which cannot be an anchor, so the browser gives it none of
 * an anchor's behavior: command-click, control-click, and shift-click all fall
 * through as plain clicks and navigate in place, losing whatever the operator
 * had open. Reading the modifiers back is what keeps a row-wide click target
 * from being a worse version of the link inside it.
 *
 * Returns nothing and does nothing when `to` is null, which is the row that has
 * no household left to open.
 */
export function openRowInNewContext(
  event: React.MouseEvent<HTMLElement>,
  to: string | null,
  navigate: NavigateFunction
): void {
  if (to === null) {
    return;
  }

  if (event.metaKey || event.ctrlKey || event.shiftKey) {
    // `noopener` keeps the opened tab from reaching back through `window.opener`.
    window.open(to, '_blank', 'noopener');
    return;
  }

  void navigate(to);
}
